/**
 * TrajectoryChart — premium cumulative-P&L area chart for System Oversight.
 *
 * Matches the app's EquityCurve visual language (bipolar green/red gradient,
 * dashed zero baseline, hover tooltips, date axis) but adds a smooth Catmull-Rom
 * curve and a dashed *forecast projection* extending past "now" to the projected
 * cumulative P&L — so the "is it heading up or down?" question is answered
 * visually, not just numerically.
 */
import React, { useMemo } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface Pt {
  date: string;
  cumulative_pnl: number;
}

const fmtPnl = (v: number) =>
  `${v >= 0 ? '+' : '-'}$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const fmtDate = (s: string) => {
  try {
    return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return s;
  }
};

/** Catmull-Rom → cubic-bezier smooth path through points. */
function smooth(pts: Array<{ x: number; y: number }>): string {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x},${pts[0].y}`;
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`;
  }
  return d;
}

export default function TrajectoryChart({
  points,
  projectedPnl,
  horizonDays = 5,
  height = 220,
}: {
  points: Pt[];
  projectedPnl?: number | null;
  horizonDays?: number;
  height?: number;
}) {
  const chart = useMemo(() => {
    if (points.length < 2) return null;

    const W = 800;
    const H = height;
    const pad = { top: 18, right: 16, bottom: 26, left: 16 };
    const plotW = W - pad.left - pad.right;
    const plotH = H - pad.top - pad.bottom;

    const hasForecast = projectedPnl != null && isFinite(projectedPnl);
    const actualFrac = hasForecast ? 0.78 : 1;

    const actualVals = points.map((p) => p.cumulative_pnl);
    const allVals = hasForecast ? [...actualVals, projectedPnl as number, 0] : [...actualVals, 0];
    const min = Math.min(...allVals);
    const max = Math.max(...allVals);
    const range = max - min || 1;

    const yOf = (v: number) => pad.top + ((max - v) / range) * plotH;
    const xActual = (i: number) => pad.left + (i / Math.max(points.length - 1, 1)) * (plotW * actualFrac);

    const coords = points.map((p, i) => ({ x: xActual(i), y: yOf(p.cumulative_pnl), point: p }));
    const line = smooth(coords);
    const baseY = pad.top + plotH;
    const area = `${line} L ${coords[coords.length - 1].x.toFixed(2)},${baseY} L ${coords[0].x.toFixed(2)},${baseY} Z`;

    const zeroY = yOf(0);
    const zeroPct = ((max - 0) / range) * 100;

    const last = coords[coords.length - 1];
    const forecastPt = hasForecast
      ? { x: pad.left + plotW, y: yOf(projectedPnl as number) }
      : null;

    return { W, H, pad, plotW, plotH, coords, line, area, zeroY, zeroPct, last, forecastPt, baseY };
  }, [points, projectedPnl, height]);

  if (!chart) {
    return (
      <div className="flex items-center justify-center text-muted-foreground" style={{ height }}>
        <p className="text-sm">Not enough closed trades to chart a trajectory yet.</p>
      </div>
    );
  }

  const { W, H, pad, plotW, coords, line, area, zeroY, zeroPct, last, forecastPt } = chart;
  const up = (coords[coords.length - 1]?.point.cumulative_pnl ?? 0) >= 0;
  const lineColor = up ? '#10b981' : '#ef4444';
  const fcUp = (projectedPnl ?? 0) >= (coords[coords.length - 1]?.point.cumulative_pnl ?? 0);

  return (
    <TooltipProvider>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="trajFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
            <stop offset={`${zeroPct}%`} stopColor="#10b981" stopOpacity={0.04} />
            <stop offset={`${zeroPct}%`} stopColor="#ef4444" stopOpacity={0.04} />
            <stop offset="100%" stopColor="#ef4444" stopOpacity={0.35} />
          </linearGradient>
        </defs>

        {/* area + line */}
        <path d={area} fill="url(#trajFill)" />
        {/* zero baseline */}
        <line x1={pad.left} y1={zeroY} x2={pad.left + plotW} y2={zeroY} stroke="#a1a1aa" strokeWidth={1} strokeDasharray="5,4" opacity={0.4} />
        <path d={line} fill="none" stroke={lineColor} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />

        {/* forecast projection */}
        {forecastPt && (
          <>
            <line x1={last.x} y1={last.y} x2={last.x} y2={pad.top} stroke="#a1a1aa" strokeWidth={1} strokeDasharray="2,4" opacity={0.35} />
            <line
              x1={last.x}
              y1={last.y}
              x2={forecastPt.x}
              y2={forecastPt.y}
              stroke={fcUp ? '#10b981' : '#ef4444'}
              strokeWidth={2}
              strokeDasharray="5,4"
              vectorEffect="non-scaling-stroke"
              opacity={0.85}
            />
            <circle cx={forecastPt.x} cy={forecastPt.y} r={4} fill={fcUp ? '#10b981' : '#ef4444'} opacity={0.9} />
          </>
        )}

        {/* endpoint marker */}
        <circle cx={last.x} cy={last.y} r={4} fill={lineColor} />

        {/* hover targets */}
        {coords.map((c, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <circle cx={c.x} cy={c.y} r={8} fill="transparent" style={{ cursor: 'pointer' }} />
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs text-muted-foreground">{fmtDate(c.point.date)}</p>
              <p className="text-xs font-bold">Cumulative {fmtPnl(c.point.cumulative_pnl)}</p>
            </TooltipContent>
          </Tooltip>
        ))}

        {/* axis labels */}
        <text x={pad.left} y={H - 4} fill="#a1a1aa" fontSize={11} textAnchor="start">{fmtDate(coords[0].point.date)}</text>
        <text x={pad.left + plotW * (forecastPt ? 0.78 : 1)} y={H - 4} fill="#a1a1aa" fontSize={11} textAnchor="middle">
          {fmtDate(coords[coords.length - 1].point.date)}
        </text>
        {forecastPt && (
          <text x={pad.left + plotW} y={H - 4} fill={fcUp ? '#10b981' : '#ef4444'} fontSize={11} textAnchor="end">
            +{horizonDays}d
          </text>
        )}
      </svg>
    </TooltipProvider>
  );
}
